import React from 'react';
import { Box, Typography, IconButton, Tooltip, Divider } from '@mui/material';
import { mdiDelete, mdiPlus, mdiClose, mdiChevronDoubleLeft, mdiChevronDoubleRight } from "@mdi/js";
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import MdiIcon from '../mdi/MdiIcon';

interface ChatHeaderProps {
  onNewChat: () => void;
  onDelete?: () => void;
  showDelete?: boolean;
  /** Whether the panel is currently widened. */
  expanded?: boolean;
  onToggleExpanded?: () => void;
  onClose?: () => void;
}

export function ChatHeader({
  onNewChat,
  onDelete,
  showDelete,
  expanded = false,
  onToggleExpanded,
  onClose,
}: ChatHeaderProps) {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // The title can be long once the panel is widened, so let the buttons
        // stay put instead of being pushed off the edge.
        gap: 1,
      }}
    >
      <Typography
        variant="h6"
        noWrap
        sx={{ fontSize: '1rem', fontWeight: 600, minWidth: 0 }}
      >
        <Trans>AI Assistant</Trans>
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
        <Tooltip title={t`New conversation`}>
          <IconButton onClick={onNewChat} size="small" aria-label={t`New conversation`}>
            <MdiIcon d={mdiPlus} />
          </IconButton>
        </Tooltip>
        {showDelete && onDelete && (
          <Tooltip title={t`Delete conversation`}>
            <IconButton onClick={onDelete} size="small" color="error" aria-label={t`Delete conversation`}>
              <MdiIcon d={mdiDelete} />
            </IconButton>
          </Tooltip>
        )}
        {onToggleExpanded && (
          <Tooltip title={expanded ? t`Narrow chat` : t`Widen chat`}>
            <IconButton
              onClick={onToggleExpanded}
              size="small"
              aria-label={expanded ? t`Narrow chat` : t`Widen chat`}
            >
              <MdiIcon d={expanded ? mdiChevronDoubleRight : mdiChevronDoubleLeft} />
            </IconButton>
          </Tooltip>
        )}
        {/* The close button is the only way to dismiss the panel by pointer:
            the backdrop does not take clicks, so clicking the page does not
            close it. */}
        {onClose && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />
            <Tooltip title={t`Close chat`}>
              <IconButton onClick={onClose} size="small" aria-label={t`Close chat`}>
                <MdiIcon d={mdiClose} />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>
    </Box>
  );
}
